import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'

import Field from '../../../../../../components/Form/Field'
import EditForm from '../../../../../../features/components/Forms/EditForm'

import { getBudgetType } from '../../../../LineItemForms/typeHelpers'
import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { formatNumber } from '../../../../../../helpers/numbers'

import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import { clearUpdateLineItem, updateLineItem } from '../../../../../../modules/actions/lineItems'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'

import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import { BUDGET_LIFETIME } from '../../../../fields'
import { BUDGET_DAILY } from '../../../../CampaignForms/fields'
import { UPDATE_COMBINED_LINE_ITEM } from '../../../../../../modules/actions/combinedData'
import { getBudgetValidation } from '../../../../../ReusableFormSections/reusableBudgetSections/remainingBudgetValidation'

const LineItemsBudgetSectionForm = ({
  adAccountId,
  campaign = {},
  lineItem = {},
  remainingBudget,
  title,
  ...formProps
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const { currency_symbol } = useSelector(selectedFacebookAdAccountSelector)
  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)

  const lineItemBudgetLifetime = lineItem[BUDGET_LIFETIME]
  const lineItemBudgetDaily = lineItem[BUDGET_DAILY]

  const { objective: campaignObjective } = campaign

  const budgetType = getBudgetType(lineItemBudgetLifetime, lineItemBudgetDaily)

  const onSubmit = useCallback(
    values => {
      const updateLineItemData = {
        account: adAccountId,
        campaign_objective: campaignObjective,
        ...(budgetType === BUDGET_LIFETIME && { budget_lifetime: values[BUDGET_LIFETIME] }),
        ...(budgetType === BUDGET_DAILY && { budget_daily: values[BUDGET_DAILY] }),
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(
        updateLineItem(updateLineItemData, lineItem.id, FACEBOOK_PLATFORM, {
          updateType: UPDATE_COMBINED_LINE_ITEM
        })
      )
    },
    [dispatch, adAccountId, campaignObjective, budgetType, lineItem.id, title]
  )

  const initialValues = useMemo(
    () => ({
      [BUDGET_LIFETIME]: String(lineItem[BUDGET_LIFETIME]) || '',
      [BUDGET_DAILY]: String(lineItem[BUDGET_DAILY]) || ''
    }),
    [lineItem]
  )

  const initialLineItemBudget = useMemo(() => lineItem[BUDGET_LIFETIME] || lineItem[BUDGET_DAILY], [lineItem])

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit,
    validationSchema: getBudgetValidation({
      remainingBudget,
      initialBudget: initialLineItemBudget,
      budgetType
    })
  })

  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={lineItemWasUpdated}
      clearEditItem={handleClearUpdateLineItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <p>{t(`Your remaining budget is ${currency_symbol}` + formatNumber(remainingBudget, { max: 2 }))}</p>
      <Field
        type="number"
        formik={formik}
        name={budgetType === BUDGET_LIFETIME ? BUDGET_LIFETIME : BUDGET_DAILY}
        placeholder={t(budgetType === BUDGET_LIFETIME ? 'Lifetime Budget' : 'Daily Budget')}
        symbol={currency_symbol}
        autoComplete="off"
      />
    </EditForm>
  )
}

export default LineItemsBudgetSectionForm
